/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { CalculationKpis } from "../models/http/calculator/calculation-kpis.model";
import { BarChart } from "../models/http/charts/bar-chart/bar-chart.model";
import { LineChart } from "../models/http/charts/line-chart/line-chart.model";
import { PieChart } from "../models/http/charts/pie-chart/pie-chart.model";
import { HardwareAssetsFile } from "../models/http/dashboard/hardware-assets-file.model";
import { HardwareAssetsGeneralInformation } from "../models/http/dashboard/hardware-assets-general-information.model";
import { HardwareAssetsOverview } from "../models/http/dashboard/hardware-assets-overview.model";
import { ManufacturerEmission } from "../models/http/dashboard/manufacturer-emission.model";
import { PollingStatus } from "../models/http/what-if/scenario/scenario-full.model";
import { ChartResponse } from "../models/ui/charts.model";
import { CioPeriod } from "../models/ui/cio-period.model";
import { EmissionViewMode } from "../models/ui/emission-view-mode.model";
import { HardwareAssetsScopeMode } from "../models/ui/hardware-assets-scope-mode.model";
import { HardwareAssetsScopeOrderBy } from "../models/ui/hardware-assets-scope-orderby.model";
import { ReportingPeriodValue } from "../models/ui/reporting-period.model";
import { SelectOption } from "../models/ui/select-option.model";
import { TimePeriodRange } from "../models/ui/time-period-range.model";
import { getReportingPeriodRange } from "../utils/reporting-period";
import { formatDateForBE, roundValueUpTo } from "../utils/utils";
import { CalculatorHardwareAssetsForm } from "./../models/http/dashboard/calculator-hardware-assets-form.model";
import { TableResponse } from "./../models/ui/table-response.model";
import { DashboardHardwareAssetsHttpService } from "./http/dashboard-hardware-assets-http.service";
import { DashboardServiceNowHttpService } from "./http/dashboard-service-now-http.service";
import { StorageService } from "./storage.service";

@Injectable({
	providedIn: "root"
})
export class DashboardHardwareAssetsService {
	private _serviceNowOverride?: boolean;
	private _serviceNowPeriod?: ReportingPeriodValue;
	private _serviceNowDateRange?: TimePeriodRange;

	constructor(
		private _dashboardHardwareAssetsHttpService: DashboardHardwareAssetsHttpService,
		private _dashboardServiceNowHttpService: DashboardServiceNowHttpService,
		private _storageService: StorageService
	) {}

	public setServiceNowOverride(value: boolean | null): void {
		this._serviceNowOverride = value === null ? undefined : value;
	}

	public setServiceNowPeriod(period: ReportingPeriodValue): void {
		this._serviceNowPeriod = period;
		this._serviceNowDateRange = undefined;
	}

	public setServiceNowDateRange(range: TimePeriodRange): void {
		this._serviceNowDateRange = range;
		this._serviceNowPeriod = undefined;
	}

	private _isServiceNowEnabled(): boolean {
		return this._serviceNowOverride ?? this._storageService.getServiceNowEnabled();
	}

	private _getServiceNowDateRange(period: CioPeriod = "FY"): {
		startDate: string;
		endDate?: string;
	} {
		if (this._serviceNowDateRange?.start) {
			return {
				startDate: formatDateForBE(this._serviceNowDateRange.start)!,
				endDate: formatDateForBE(this._serviceNowDateRange.end)
			};
		}

		if (this._serviceNowPeriod) {
			const { start, end } = getReportingPeriodRange(this._serviceNowPeriod);
			return {
				startDate: formatDateForBE(start)!,
				endDate: formatDateForBE(end)
			};
		}

		const now = new Date();
		const endOfDay = (date: Date) =>
			new Date(
				Date.UTC(
					date.getUTCFullYear(),
					date.getUTCMonth(),
					date.getUTCDate(),
					23,
					59,
					59,
					999
				)
			);
		const addMonths = (date: Date, months: number) =>
			new Date(
				Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + months, date.getUTCDate())
			);
		const fiscalYearStartYear =
			now.getUTCMonth() >= 3 ? now.getUTCFullYear() : now.getUTCFullYear() - 1;

		const fiscalYearStart = new Date(Date.UTC(fiscalYearStartYear, 3, 1));
		const fiscalYearEnd = new Date(Date.UTC(fiscalYearStartYear + 1, 2, 31, 23, 59, 59, 999));

		const fiscalMonth = (now.getUTCMonth() + 9) % 12;
		const fiscalQuarter = Math.floor(fiscalMonth / 3);
		const fiscalQuarterStartMonth = fiscalQuarter * 3;
		const fiscalQuarterStartCalendarMonth = (fiscalQuarterStartMonth + 3) % 12;
		const fiscalQuarterStartYear =
			fiscalQuarterStartCalendarMonth < 3 ? fiscalYearStartYear + 1 : fiscalYearStartYear;
		const fiscalQuarterStart = new Date(
			Date.UTC(fiscalQuarterStartYear, fiscalQuarterStartCalendarMonth, 1)
		);
		const nextFiscalQuarterStart = addMonths(fiscalQuarterStart, 3);
		const fiscalQuarterEnd = new Date(
			Date.UTC(
				nextFiscalQuarterStart.getUTCFullYear(),
				nextFiscalQuarterStart.getUTCMonth(),
				0,
				23,
				59,
				59,
				999
			)
		);

		const fiscalSemester = fiscalMonth < 6 ? 0 : 1;
		const fiscalSemesterStartMonth = fiscalSemester * 6;
		const fiscalSemesterStartCalendarMonth = (fiscalSemesterStartMonth + 3) % 12;
		const fiscalSemesterStartYear =
			fiscalSemesterStartCalendarMonth < 3 ? fiscalYearStartYear + 1 : fiscalYearStartYear;
		const fiscalSemesterStart = new Date(
			Date.UTC(fiscalSemesterStartYear, fiscalSemesterStartCalendarMonth, 1)
		);
		const nextFiscalSemesterStart = addMonths(fiscalSemesterStart, 6);
		const fiscalSemesterEnd = new Date(
			Date.UTC(
				nextFiscalSemesterStart.getUTCFullYear(),
				nextFiscalSemesterStart.getUTCMonth(),
				0,
				23,
				59,
				59,
				999
			)
		);

		switch (period) {
			case "CURR_Y": {
				const startDate = new Date(Date.UTC(now.getUTCFullYear(), 0, 1));
				return {
					startDate: formatDateForBE(startDate)!,
					endDate: formatDateForBE(endOfDay(now))!
				};
			}
			case "CURR_Q":
				return {
					startDate: formatDateForBE(fiscalQuarterStart)!,
					endDate: formatDateForBE(endOfDay(now))!
				};
			case "CURR_S":
				return {
					startDate: formatDateForBE(fiscalSemesterStart)!,
					endDate: formatDateForBE(endOfDay(now))!
				};
			case "LAST_Y": {
				const lastFiscalStart = new Date(Date.UTC(fiscalYearStartYear - 1, 3, 1));
				const lastFiscalEnd = new Date(
					Date.UTC(fiscalYearStartYear, 2, 31, 23, 59, 59, 999)
				);
				return {
					startDate: formatDateForBE(lastFiscalStart)!,
					endDate: formatDateForBE(lastFiscalEnd)!
				};
			}
			case "LAST_Q": {
				const lastQuarterStart = addMonths(fiscalQuarterStart, -3);
				const lastQuarterEnd = new Date(
					Date.UTC(
						fiscalQuarterStart.getUTCFullYear(),
						fiscalQuarterStart.getUTCMonth(),
						0,
						23,
						59,
						59,
						999
					)
				);
				return {
					startDate: formatDateForBE(lastQuarterStart)!,
					endDate: formatDateForBE(lastQuarterEnd)!
				};
			}
			case "LAST_S": {
				const lastSemesterStart = addMonths(fiscalSemesterStart, -6);
				const lastSemesterEnd = new Date(
					Date.UTC(
						fiscalSemesterStart.getUTCFullYear(),
						fiscalSemesterStart.getUTCMonth(),
						0,
						23,
						59,
						59,
						999
					)
				);
				return {
					startDate: formatDateForBE(lastSemesterStart)!,
					endDate: formatDateForBE(lastSemesterEnd)!
				};
			}
			case "FY":
			default:
				return {
					startDate: formatDateForBE(fiscalYearStart)!,
					endDate: formatDateForBE(fiscalYearEnd)!
				};
		}
	}

	/**
	 * Renames a file associated with a specific tenant.
	 *
	 * @param fileId - The unique identifier of the file to be renamed.
	 * @param tenantId - The unique identifier of the tenant associated with the file.
	 * @param name - The new name to assign to the file.
	 * @returns An observable that emits the response as a string upon successful renaming.
	 */
	public renameFile(fileId: string, tenantId: string, name: string): Observable<string> {
		return this._dashboardHardwareAssetsHttpService.renameFile(fileId, tenantId, name);
	}

	/**
	 * Deletes a file associated with a specific tenant.
	 *
	 * @param fileId - The unique identifier of the file to be deleted.
	 * @param tenantId - The unique identifier of the tenant associated with the file.
	 * @returns An observable that emits the response as a string upon successful deletion.
	 */
	public deleteFile(fileId: string, tenantId: string): Observable<string> {
		return this._dashboardHardwareAssetsHttpService.deleteFile(fileId, tenantId);
	}

	/**
	 * Uploads a file associated with a specific tenant.
	 *
	 * @param tenantId - The unique identifier of the tenant.
	 * @param file - The file to be uploaded, wrapped in a FormData object.
	 * @param isTemporary - Optional flag indicating if the upload is temporary.
	 * @returns An Observable emitting an object containing the uploaded file's ID.
	 */
	public uploadFile(
		tenantId: string,
		file: FormData,
		isTemporary?: boolean
	): Observable<{ fileId: string }> {
		return this._dashboardHardwareAssetsHttpService.uploadFile(tenantId, file, isTemporary);
	}

	/**
	 * Initiates the download of hardware asset data as a CSV file for a specific tenant.
	 *
	 * @param fileId - The unique identifier of the file to be downloaded.
	 * @param tenantId - The unique identifier of the tenant requesting the download.
	 * @returns An Observable that emits the CSV file content as a string.
	 */
	public downloadAsCsv(fileId: string, tenantId: string): Observable<string> {
		return this._dashboardHardwareAssetsHttpService.downloadAsCsv(fileId, tenantId);
	}

	/**
	 * Retrieves the uploading status of a file for a specific tenant.
	 *
	 * @param fileId - The unique identifier of the file whose uploading status is being queried.
	 * @param tenantId - The unique identifier of the tenant associated with the file.
	 * @returns An observable that emits the polling status of the file upload.
	 */
	public getUploadingStatus(
		fileId: string,
		tenantId: string
	): Observable<{ status: PollingStatus; message?: string }> {
		return this._dashboardHardwareAssetsHttpService.getUploadingStatus(fileId, tenantId);
	}

	/**
	 * Downloads a template file from the server.
	 *
	 * @returns An `Observable` that emits a `Blob` representing the downloaded template file.
	 */
	public downloadTemplate(): Observable<Blob> {
		return this._dashboardHardwareAssetsHttpService.downloadTemplate();
	}

	/**
	 * Retrieves a list of device files associated with a specific tenant.
	 *
	 * @param tenantId - The unique identifier of the tenant for which the files are to be retrieved.
	 * @returns An observable that emits an array of `HardwareAssetsFile` objects.
	 */
	public getFiles(tenantId: string): Observable<{ files: HardwareAssetsFile[] }> {
		return this._dashboardHardwareAssetsHttpService.getFiles(tenantId);
	}

	/**
	 * Retrieves a list of device categories from the dashboard service.
	 *
	 * @param tenantId - The unique identifier of the tenant for which the files are to be retrieved.
	 * @returns An observable that emits an array of category strings.
	 */
	public getCategories(tenantId: string): Observable<{ categories: string[] }> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService.getDevicesCategories(
				tenantId,
				startDate,
				endDate
			);
		}

		return this._dashboardHardwareAssetsHttpService.getCategories(tenantId);
	}

	/**
	 * Retrieves general information about hardware assets based on the provided file ID and tenant ID.
	 *
	 * @param fileId - The unique identifier of the file for which general information is requested.
	 * @param tenantId - The unique identifier of the tenant associated with the request.
	 * @returns An observable that emits the general information of hardware assets as a `HardwareAssetsGeneralInformation` object.
	 */
	public getGeneralInformation(
		fileId: string,
		tenantId: string
	): Observable<HardwareAssetsGeneralInformation> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getGeneralInformation(tenantId, startDate, endDate)
				.pipe(
					map(response => ({
						totalEmission: response.totalEmission,
						scope2Emission: response.scope2Emission,
						scope3Emission: response.scope3Emission,
						totalAssetsCount: response.totalAssetsCount,
						accountedAssetsInReportingPeriod: response.accountedAssetsInReportingPeriod,
						manufacturersCount: response.manufacturersCount ??
							response.manufacterCount ?? {
								label: "Manufacturers",
								value: 0,
								unit: ""
							}
					})),
					catchError(() =>
						of({
							totalEmission: {
								label: "Total Emissions",
								value: 0,
								unit: "kgCO2e"
							},
							scope2Emission: {
								label: "Scope 2 Emissions",
								value: 0,
								unit: "kgCO2e"
							},
							scope3Emission: {
								label: "Scope 3 Emissions",
								value: 0,
								unit: "kgCO2e"
							},
							totalAssetsCount: {
								label: "Total Assets",
								value: 0,
								unit: "pcs"
							},
							accountedAssetsInReportingPeriod: {
								label: "Accounted Assets in Reporting Period",
								value: 0,
								unit: "pcs"
							},
							manufacturersCount: {
								label: "Manufacturers",
								value: 0,
								unit: ""
							}
						})
					)
				);
		}

		return this._dashboardHardwareAssetsHttpService.getGeneralInformation(fileId, tenantId);
	}

	/**
	 * Retrieves the total emissions data for a pie chart visualization.
	 *
	 * @param fileId - The unique identifier of the file containing the data.
	 * @param tenantId - The unique identifier of the tenant requesting the data.
	 * @param categories - An array of categories to filter the emissions data.
	 * @returns An observable emitting a `ChartResponse` containing the pie chart data or an error state.
	 *
	 * The method constructs HTTP parameters and path parameters, then makes a GET request
	 * using the `_communicationService`. The response is mapped to a `ChartResponse` object
	 * containing the pie chart data. If an error occurs during the request, it returns a
	 * default error state with an empty pie chart object.
	 */
	public getTotalEmissionForPieChart(
		fileId: string,
		tenantId: string,
		categories: string[]
	): Observable<ChartResponse<PieChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.postManufacturerEmissionPie(tenantId, startDate, endDate, { categories })
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as PieChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getTotalEmissionForPieChart(
			fileId,
			tenantId,
			categories
		);
	}

	public getEmissionsByModel(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getDevicesModelsBar(tenantId, startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as BarChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getEmissionsByModel(fileId, tenantId);
	}

	public getEmissionsByModelPerDevice(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getDevicesModelsBarPerDevice(tenantId, startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as BarChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getEmissionsByModelPerDevice(
			fileId,
			tenantId
		);
	}

	public getEmissionsByRegion(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getDevicesLocationBar(tenantId, startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as BarChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getEmissionsByRegion(fileId, tenantId);
	}

	public getEmissionsByRegionPerDevice(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getDevicesLocationBarPerDevice(tenantId, startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as BarChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getEmissionsByRegionPerDevice(
			fileId,
			tenantId
		);
	}

	public getEmissionsOverTime(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<LineChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getTotalDevicesOverTime(tenantId, startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as LineChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getEmissionsOverTime(fileId, tenantId);
	}

	public getSavedLifecycleEmissions(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<LineChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getLifecycleSavingsDevicesOverTime(tenantId, startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as LineChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getSavedLifecycleEmissions(
			fileId,
			tenantId
		);
	}

	public getEmissionsByCountryOverTime(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<LineChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getLocationDevicesOverTime(tenantId, startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as LineChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getEmissionsByCountryOverTime(
			fileId,
			tenantId
		);
	}

	/**
	 * Retrieves the CO2 emissions data by each installation status group
	 * for a pie chart visualization.
	 *
	 * @param fileId - The unique identifier of the file containing the data.
	 * @param tenantId - The unique identifier of the tenant requesting the data.
	 * @param categories - An array of categories to filter the emissions data.
	 * @returns An observable emitting a `ChartResponse` containing the pie chart data or an error state.
	 *
	 * The method constructs HTTP parameters and path parameters, then makes a GET request
	 * using the `_communicationService`. The response is mapped to a `ChartResponse` object
	 * containing the pie chart data. If an error occurs during the request, it returns a
	 * default error state with an empty pie chart object.
	 */
	public getCo2ByInstallationStatusForPieChart(
		fileId: string
	): Observable<ChartResponse<PieChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getDeviceStatusEmissionPie(this._storageService.getTenantId(), startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as PieChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getCo2ByInstallStatusForPieChart(fileId);
	}

	/**
	 * Retrieves the manufacturers' emissions data for a specific file and tenant.
	 *
	 * @param fileId - The unique identifier of the file.
	 * @param tenantId - The unique identifier of the tenant.
	 * @param categories - An array of categories to filter the emissions data.
	 * @returns An observable that emits an array of `ManufacturerEmission` objects.
	 *          If an error occurs, an empty array is returned.
	 */
	public getManufacturersEmission(
		fileId: string,
		tenantId: string,
		categories: string[]
	): Observable<TableResponse<ManufacturerEmission[]>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getManufacturerEmissionTable(tenantId, startDate, endDate, { categories })
				.pipe(
					map(({ manufacturersEmissions }) => {
						const mappedManufacturersEmissions = manufacturersEmissions.map(
							manufacturer => ({
								...manufacturer,
								emissionValue: roundValueUpTo(manufacturer.emissionValue)
							})
						);

						return {
							rowData: mappedManufacturersEmissions,
							hasError: false
						};
					}),
					catchError(() => of({ rowData: [], hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getManufacturersEmission(
			fileId,
			tenantId,
			categories
		);
	}

	/**
	 * Retrieves the carbon footprint data for a specific file and tenant.
	 *
	 * @param fileId - The unique identifier of the file for which the carbon footprint is requested.
	 * @param tenantId - The unique identifier of the tenant associated with the request.
	 * @returns An observable that emits a `ChartResponse` containing a `BarChart` object if successful,
	 *          or an object with an empty `BarChart` and `hasError` set to true in case of an error.
	 */
	public getCarbonFootprint(
		fileId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getDeviceCarbonFootprint(tenantId, startDate, endDate)
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as BarChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getCarbonFootprint(fileId, tenantId);
	}

	/**
	 * Retrieves the carbon footprint data by scope for a specific file and tenant.
	 *
	 * @param fileId - The unique identifier of the file for which the carbon footprint data is requested.
	 * @param tenantId - The unique identifier of the tenant associated with the data.
	 * @param mode - The mode specifying the scope of the hardware assets (e.g., specific filtering criteria).
	 * @param orderBy - The ordering criteria for the data (e.g., by date, value, etc.).
	 * @returns An observable that emits a `ChartResponse` containing a `BarChart` object with the carbon footprint data.
	 *          If an error occurs, the observable emits a response with an empty chart and `hasError` set to true.
	 */
	public getCarbonFootprintByScope(
		fileId: string,
		tenantId: string,
		mode: HardwareAssetsScopeMode,
		orderBy: HardwareAssetsScopeOrderBy,
		manufacturers: string[]
	): Observable<ChartResponse<BarChart>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.postScopeCarbonFootprintBar(tenantId, startDate, endDate, mode, orderBy, {
					manufacturers
				})
				.pipe(
					map(response => ({ chart: response, hasError: false })),
					catchError(() => of({ chart: {} as BarChart, hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getCarbonFootprintByScope(
			fileId,
			tenantId,
			mode,
			orderBy,
			manufacturers
		);
	}

	/**
	 * Retrieves a list of manufacturers associated with a specific file and tenant.
	 *
	 * @param fileId - The unique identifier of the file for which manufacturers are being retrieved.
	 * @param tenantId - The unique identifier of the tenant associated with the request.
	 * @returns An observable that emits an array of manufacturer names.
	 */
	public getManufacturers(
		fileId: string,
		tenantId: string
	): Observable<TableResponse<{ manufacturer: string }[]>> {
		if (this._isServiceNowEnabled()) {
			const { startDate, endDate } = this._getServiceNowDateRange();
			return this._dashboardServiceNowHttpService
				.getScopeCarbonFootprintTable(tenantId, startDate, endDate)
				.pipe(
					map(({ manufacturers }) => ({
						rowData: manufacturers.map(manufacturer => ({ manufacturer })),
						hasError: false
					})),
					catchError(() => of({ rowData: [], hasError: true }))
				);
		}

		return this._dashboardHardwareAssetsHttpService.getManufacturers(fileId, tenantId);
	}

	/**
	 * Retrieves a scenario identifier based on the provided list of file names.
	 *
	 * @param files - An array of file names to be used for scenario retrieval.
	 * @returns An Observable that emits an object containing the `scenarioId` as a string.
	 */
	public getScenario(files: string[]): Observable<{ scenarioId: string }> {
		return this._dashboardHardwareAssetsHttpService.getScenario(files);
	}

	/**
	 * Retrieves the status of a specific scenario for a given tenant.
	 *
	 * @param scenarioId - The unique identifier of the scenario whose status is to be fetched.
	 * @param tenantId - The unique identifier of the tenant associated with the scenario.
	 * @returns An Observable emitting an object containing the scenario's polling status and an optional message.
	 */
	public getScenarioStatus(
		scenarioId: string,
		tenantId: string
	): Observable<{ status: PollingStatus; message?: string }> {
		return this._dashboardHardwareAssetsHttpService.getScenarioStatus(scenarioId, tenantId);
	}

	/**
	 * Deletes a hardware asset scenario for a specific tenant.
	 *
	 * @param scenarioId - The unique identifier of the scenario to delete.
	 * @param tenantId - The unique identifier of the tenant associated with the scenario.
	 * @returns An Observable that emits the ID of the deleted scenario upon successful deletion.
	 */
	public deleteScenario(scenarioId: string, tenantId: string): Observable<string> {
		return this._dashboardHardwareAssetsHttpService.deleteScenario(scenarioId, tenantId);
	}

	/**
	 * Retrieves the emission overview data for a specific scenario and tenant, formatted for a bar chart.
	 *
	 * @param scenarioId - The unique identifier of the scenario to retrieve emission data for.
	 * @param tenantId - The unique identifier of the tenant whose emission data is requested.
	 * @param viewMode - The mode in which the emission data should be viewed (e.g., by category, by time period).
	 * @returns An Observable emitting a ChartResponse containing BarChart data representing the emission overview.
	 */
	public getEmissionOverviewForBarChart(
		scenarioId: string,
		tenantId: string,
		viewMode: EmissionViewMode
	): Observable<ChartResponse<BarChart>> {
		return this._dashboardHardwareAssetsHttpService.getEmissionOverviewForBarChart(
			scenarioId,
			tenantId,
			viewMode
		);
	}

	/**
	 * Retrieves the total emission data grouped by category for a given scenario and tenant.
	 *
	 * @param scenarioId - The unique identifier of the scenario for which emissions are to be fetched.
	 * @param tenantId - The unique identifier of the tenant.
	 * @returns An Observable emitting a ChartResponse containing BarChart data representing total emissions by category.
	 */
	public getTotalEmissionByCategory(
		scenarioId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		return this._dashboardHardwareAssetsHttpService.getTotalEmissionByCategory(
			scenarioId,
			tenantId
		);
	}

	/**
	 * Retrieves the total emissions data grouped by manufacturer for a given scenario and tenant.
	 *
	 * @param scenarioId - The unique identifier of the scenario to retrieve data for.
	 * @param tenantId - The unique identifier of the tenant.
	 * @param mode - The scope mode for hardware assets (e.g., global, local).
	 * @param orderBy - The ordering criteria for the results.
	 * @returns An Observable emitting a ChartResponse containing BarChart data representing total emissions by manufacturer.
	 */
	public getTotalEmissionByManufacturer(
		scenarioId: string,
		tenantId: string,
		mode: HardwareAssetsScopeMode,
		orderBy: HardwareAssetsScopeOrderBy
	): Observable<ChartResponse<BarChart>> {
		return this._dashboardHardwareAssetsHttpService.getTotalEmissionByManufacturer(
			scenarioId,
			tenantId,
			mode,
			orderBy
		);
	}

	/**
	 * Retrieves a list of all available countries.
	 *
	 * @returns An Observable that emits an object containing an array of country names.
	 */
	public getAllCountries(): Observable<SelectOption[]> {
		return this._dashboardHardwareAssetsHttpService.getAllCountries();
	}

	/**
	 * Retrieves a list of all hardware asset manufacturers.
	 *
	 * @returns An Observable that emits an object containing an array of manufacturer names.
	 */
	public getAllManufacturers(): Observable<{ manufacturers: string[] }> {
		return this._dashboardHardwareAssetsHttpService.getAllManufacturers();
	}

	/**
	 * Computes hardware asset calculations by sending the provided form data to the backend service.
	 *
	 * @param hardwareAssets - The form data containing hardware assets information to be computed.
	 * @returns An Observable emitting an object with the generated calculation ID.
	 */
	public compute(
		hardwareAssets: CalculatorHardwareAssetsForm[]
	): Observable<{ calculationId: string }> {
		return this._dashboardHardwareAssetsHttpService.compute(hardwareAssets);
	}

	/**
	 * Retrieves the list of general hardware asset categories.
	 *
	 * @returns An Observable that emits an object containing an array of category names.
	 */
	public getGeneralCategories(): Observable<{ categories: string[] }> {
		return this._dashboardHardwareAssetsHttpService.getGeneralCategories();
	}

	/**
	 * Retrieves specific hardware asset categories based on the provided category name.
	 *
	 * @param category - The name of the category to filter hardware asset categories.
	 * @returns An Observable emitting an object containing an array of category names.
	 */
	public getSpecificCategories(category: string): Observable<{ categories: string[] }> {
		return this._dashboardHardwareAssetsHttpService.getSpecificCategories(category);
	}

	/**
	 * Retrieves the total emission data for a given calculation and tenant.
	 *
	 * @param calculationId - The unique identifier for the emission calculation.
	 * @param tenantId - The unique identifier for the tenant.
	 * @returns An Observable emitting a ChartResponse containing BarChart data representing total emissions.
	 */
	public getTotalEmission(
		calculationId: string,
		tenantId: string
	): Observable<ChartResponse<BarChart>> {
		return this._dashboardHardwareAssetsHttpService.getTotalEmission(calculationId, tenantId);
	}

	/**
	 * Retrieves the emission overview for hardware assets based on the specified calculation and tenant IDs.
	 *
	 * @param calculationId - The unique identifier for the emission calculation.
	 * @param tenantId - The unique identifier for the tenant.
	 * @returns An Observable emitting an array of `HardwareAssetsOverview` objects representing the emission overview.
	 */
	public getEmissionOverview(
		calculationId: string,
		tenantId: string
	): Observable<HardwareAssetsOverview[]> {
		return this._dashboardHardwareAssetsHttpService.getEmissionOverview(
			calculationId,
			tenantId
		);
	}

	/**
	 * Retrieves the calculation KPIs for a specific calculation and tenant.
	 *
	 * @param calculationId - The unique identifier of the calculation.
	 * @param tenantId - The unique identifier of the tenant.
	 * @returns An Observable emitting the calculation KPIs.
	 */
	public getCalculationKpis(
		calculationId: string,
		tenantId: string
	): Observable<CalculationKpis> {
		return this._dashboardHardwareAssetsHttpService.getCalculationKpis(calculationId, tenantId);
	}

	/**
	 * Retrieves emission data categorized by scope for a given calculation and tenant.
	 *
	 * @param calculationId - The unique identifier for the emission calculation.
	 * @param tenantId - The unique identifier for the tenant.
	 * @returns An Observable emitting a ChartResponse containing PieChart data representing emissions by scope.
	 */
	public getEmissionByScope(
		calculationId: string,
		tenantId: string
	): Observable<ChartResponse<PieChart>> {
		return this._dashboardHardwareAssetsHttpService.getEmissionByScope(calculationId, tenantId);
	}

	/**
	 * Retrieves emission data grouped by manufacturer for a given calculation and tenant.
	 *
	 * @param calculationId - The unique identifier for the emission calculation.
	 * @param tenantId - The unique identifier for the tenant.
	 * @returns An Observable emitting a PieChart representing emissions by manufacturer.
	 */
	public getEmissionByManufacturer(
		calculationId: string,
		tenantId: string
	): Observable<ChartResponse<PieChart>> {
		return this._dashboardHardwareAssetsHttpService.getEmissionByManufacturer(
			calculationId,
			tenantId
		);
	}

	/**
	 * Saves a hardware asset calculation for a specific tenant.
	 *
	 * @param calculationId - The unique identifier of the calculation to be saved.
	 * @param tenantId - The unique identifier of the tenant for whom the calculation is being saved.
	 * @returns An Observable that emits an object containing the generated fileId upon successful save.
	 */
	public saveCalculation(
		calculationId: string,
		tenantId: string
	): Observable<{ fileId: string }> {
		return this._dashboardHardwareAssetsHttpService.saveCalculation(calculationId, tenantId);
	}

	/**
	 * Deletes a calculation by its unique identifier.
	 *
	 * @param calculationId - The unique identifier of the calculation to delete.
	 * @returns An Observable that emits the ID of the deleted calculation upon successful deletion.
	 */
	public deleteCalculation(calculationId: string): Observable<string> {
		return this._dashboardHardwareAssetsHttpService.deleteCalculation(calculationId);
	}

	/**
	 * Retrieves the calculation status for a given calculation ID.
	 *
	 * @param calculationId - The unique identifier of the calculation whose status is to be retrieved.
	 * @returns An Observable emitting an object containing the current polling status and an optional message.
	 */
	public getCalculationStatus(
		calculationId: string
	): Observable<{ status: PollingStatus; message?: string }> {
		return this._dashboardHardwareAssetsHttpService.getCalculationStatus(calculationId);
	}
}
